import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Page,
  Layout,
  Card,
  DataTable,
  Button,
  Modal,
  FormLayout,
  TextField,
  EmptyState,
  Spinner,
  Banner,
  Text,
  Autocomplete,
  ButtonGroup,
  Tooltip
} from '@shopify/polaris';
import Toast from '../components/Toast';
import { DeleteMajor } from '@shopify/polaris-icons';
import { api } from '../utils/api';
import { variantOptionsFor, loadAllVariants } from '../utils/variants';
import { savedMessage } from '../utils/checkoutSync';
import TagPicker, { displayTag, useTagSuggestions } from '../components/TagPicker';
import VariantSelect from '../components/VariantSelect';
import ExpiryPicker from '../components/ExpiryPicker';
import ProductPicker from '../components/ProductPicker';

// An override as the list edits it: product, limit ('' = none), variant ('' = all),
// reason and expiry date (YYYY-MM-DD, '' = never). Expiries are stored as midnight
// UTC of the chosen date, so the UTC date is the one the merchant picked. A draft
// for a newly picked product also carries that product's variants
// (productVariants), which isn't compared when deciding whether to show Save.
const savedDraft = (override) => ({
  productId: override.productId,
  productTitle: override.productTitle || '',
  newLimit: override.overrideType === 'block' || override.overrideType === 'unlimited'
    ? ''
    : String(override.newLimit ?? ''),
  variantId: override.variantId || '',
  reason: override.reason || '',
  expiresAt: override.expiresAt ? new Date(override.expiresAt).toISOString().slice(0, 10) : ''
});

// Centre a DataTable header or cell. Polaris DataTable has no per-column centre
// option (only left/right via columnContentTypes), so wrap the content. Flex
// (not just text-align) so it also centres non-text cells like the action buttons.
const center = (content) => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{content}</div>
);

function Customers() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overrides, setOverrides] = useState([]);
  const [modalActive, setModalActive] = useState(false);
  // Unsaved edits to rows in the list, by override id (see savedDraft). A row's
  // Save button shows while its draft differs from what's saved.
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState(null);
  // A dialog's button shows a spinner while its save or removal runs, so the
  // pause before it closes doesn't look like nothing happened.
  const [dialogBusy, setDialogBusy] = useState(false);
  const whileBusy = (action) => async () => {
    setDialogBusy(true);
    try {
      await action();
    } finally {
      setDialogBusy(false);
    }
  };
  const [editingReasonId, setEditingReasonId] = useState(null);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [overrideToDelete, setOverrideToDelete] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  // Tag overrides are switched on per shop; without the switch the page is unchanged.
  const customerTags = useTagSuggestions('customer');
  const productTags = useTagSuggestions('product');
  const [targetType, setTargetType] = useState('customer');
  const [tag, setTag] = useState('');
  // What the override covers: one product, or every product with a product tag.
  const [coverType, setCoverType] = useState('product');
  const [productTag, setProductTag] = useState('');

  // Customer search state
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [skipCustomerSearch, setSkipCustomerSearch] = useState(false);
  const [customerSearchEmpty, setCustomerSearchEmpty] = useState(false);

  // Product search state
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantOptions, setVariantOptions] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [skipProductSearch, setSkipProductSearch] = useState(false);
  const [productSearchEmpty, setProductSearchEmpty] = useState(false);
  // The product last picked in the add dialog, so its full variant list isn't
  // overwritten by a slower load for a product picked before it.
  const pickedProductRef = useRef(null);

  // Override form state
  const [formData, setFormData] = useState({
    customerEmail: '',
    customerId: '',
    customerName: '',
    productId: '',
    productTitle: '',
    variantId: '',
    variantTitle: '',
    newLimit: '',
    reason: '',
    expiresAt: ''
  });

  useEffect(() => {
    loadOverrides();
  }, []);

  // Debounced customer search
  const searchCustomers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setCustomerOptions([]);
      setCustomerSearchEmpty(false);
      return;
    }
    try {
      setCustomerSearchLoading(true);
      setCustomerSearchEmpty(false);
      const response = await api.get(`/customers/search?query=${encodeURIComponent(query)}`);
      const options = response.data.map(customer => ({
        value: customer.customerId,
        label: `${customer.firstName || ''} ${customer.lastName || ''} (${customer.customerEmail})`.trim(),
        customer: customer
      }));
      setCustomerOptions(options);
      setCustomerSearchEmpty(options.length === 0);
    } catch (err) {
      console.error('Customer search error:', err);
      setCustomerOptions([]);
      setCustomerSearchEmpty(true);
    } finally {
      setCustomerSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip search if flag is set (value was set programmatically)
    if (skipCustomerSearch) {
      setSkipCustomerSearch(false);
      return;
    }
    const timer = setTimeout(() => {
      searchCustomers(customerSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearchQuery, searchCustomers, skipCustomerSearch]);

  const handleCustomerSelect = useCallback((selected) => {
    if (selected.length > 0) {
      const option = customerOptions.find(opt => opt.value === selected[0]);
      if (option) {
        setSelectedCustomer(option);
        setSkipCustomerSearch(true); // Don't trigger search when setting the value
        setCustomerSearchQuery(option.label); // Show selected customer in search box
        setFormData(prev => ({
          ...prev,
          customerId: option.customer.customerId,
          customerEmail: option.customer.customerEmail,
          customerName: `${option.customer.firstName || ''} ${option.customer.lastName || ''}`.trim()
        }));
      }
    } else {
      setSelectedCustomer(null);
    }
  }, [customerOptions]);

  // Debounced product search
  const searchProducts = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setProductOptions([]);
      setProductSearchEmpty(false);
      return;
    }
    try {
      setProductSearchLoading(true);
      setProductSearchEmpty(false);
      const response = await api.get(`/products/search/shopify?query=${encodeURIComponent(query)}`);
      const options = response.data.map(product => ({
        value: product.id,
        label: product.title,
        product: product
      }));
      setProductOptions(options);
      setProductSearchEmpty(options.length === 0);
    } catch (err) {
      console.error('Product search error:', err);
      setProductOptions([]);
      setProductSearchEmpty(true);
    } finally {
      setProductSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip search if flag is set (value was set programmatically)
    if (skipProductSearch) {
      setSkipProductSearch(false);
      return;
    }
    const timer = setTimeout(() => {
      searchProducts(productSearchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearchQuery, searchProducts, skipProductSearch]);

  const handleProductSelect = useCallback((selected) => {
    if (selected.length > 0) {
      const option = productOptions.find(opt => opt.value === selected[0]);
      if (option) {
        setSelectedProduct(option);
        setSkipProductSearch(true); // Don't trigger search when setting the value
        setProductSearchQuery(option.label); // Show selected product in search box

        // Show the variants the search returned straight away, then every variant
        // once loaded (search results only carry a product's first few). A later
        // pick of another product wins if the lists arrive out of order.
        pickedProductRef.current = option.product.id;
        setVariantOptions(variantOptionsFor(option.product.variants));
        loadAllVariants(option.product)
          .then((variants) => {
            if (pickedProductRef.current === option.product.id) {
              setVariantOptions(variantOptionsFor(variants));
            }
          })
          .catch((err) => {
            console.error('Load variants error:', err);
            setError('Failed to load all of this product\'s variants. Please pick the product again.');
          });
        setSelectedVariant(null);

        setFormData(prev => ({
          ...prev,
          productId: option.product.id,
          productTitle: option.product.title,
          variantId: '',
          variantTitle: ''
        }));
      }
    } else {
      setSelectedProduct(null);
      setVariantOptions([]);
      setSelectedVariant(null);
    }
  }, [productOptions]);

  const handleVariantSelect = useCallback((selected) => {
    if (selected.length > 0) {
      const option = variantOptions.find(opt => opt.value === selected[0]);
      if (option) {
        setSelectedVariant(option);
        setFormData(prev => ({
          ...prev,
          variantId: option.value,
          variantTitle: option.label !== 'All Variants' ? option.label : ''
        }));
      }
    } else {
      setSelectedVariant(null);
      setFormData(prev => ({
        ...prev,
        variantId: '',
        variantTitle: ''
      }));
    }
  }, [variantOptions]);

  const loadOverrides = async () => {
    try {
      setLoading(true);
      setError(null);
      // This would load all customer overrides from the API
      // For now, we'll use an empty array as placeholder
      const response = await api.get('/customers/overrides').catch(() => ({ data: [] }));
      setOverrides(response.data);
    } catch (err) {
      console.error('Load overrides error:', err);
      setError('Failed to load customer overrides');
    } finally {
      setLoading(false);
    }
  };

  const handleModalOpen = () => {
    setFormData({
      customerEmail: '',
      customerId: '',
      customerName: '',
      productId: '',
      productTitle: '',
      variantId: '',
      variantTitle: '',
      newLimit: '',
      reason: '',
      expiresAt: ''
    });
    setCustomerSearchQuery('');
    setSelectedCustomer(null);
    setCustomerOptions([]);
    setCustomerSearchEmpty(false);
    setProductSearchQuery('');
    setSelectedProduct(null);
    setProductOptions([]);
    setProductSearchEmpty(false);
    setVariantOptions([]);
    setSelectedVariant(null);
    setTargetType('customer');
    setTag('');
    setCoverType('product');
    setProductTag('');
    setModalActive(true);
  };

  const handleModalClose = () => {
    setModalActive(false);
  };

  const handleSave = async () => {
    try {
      // Infer override type from newLimit value
      const overrideType = formData.newLimit ? 'increase' : 'block';
      const target = targetType === 'tag'
        ? { targetType: 'tag', tag }
        : { targetType: 'customer' };
      const covers = coverType === 'tag'
        ? { productTag, productId: '', productTitle: '', variantId: '', variantTitle: '' }
        : {};
      const response = await api.post('/customers/override', {
        ...formData,
        ...target,
        ...covers,
        overrideType
      });
      showToast(savedMessage(response, 'Customer override created successfully'));
      handleModalClose();
      loadOverrides();
    } catch (err) {
      console.error('Save override error:', err);
      setError('Failed to save customer override');
    }
  };

  const draftFor = (override) => ({ ...savedDraft(override), ...drafts[override._id] });

  const updateDraft = (override, change) => {
    setDrafts(prev => ({ ...prev, [override._id]: { ...draftFor(override), ...change } }));
  };

  const isDirty = (override) => {
    const draft = draftFor(override);
    const saved = savedDraft(override);
    return Object.keys(saved).some((key) => draft[key] !== saved[key]);
  };

  // Save a row's edits: limit, variant, reason and expiry together.
  const handleRowSave = async (override) => {
    const draft = draftFor(override);
    if (draft.newLimit !== '' && !(parseInt(draft.newLimit, 10) >= 0)) {
      setError('Enter a limit of 0 or more, or leave it empty.');
      return;
    }

    setSavingId(override._id);
    try {
      const path = override.targetType === 'tag'
        ? `/customers/tag-override/${override._id}`
        : `/customers/${override.customerId}/override/${override._id}`;
      // An empty limit keeps an unlimited override unlimited and blocks otherwise,
      // the same as the add dialog.
      const overrideType = draft.newLimit !== ''
        ? 'increase'
        : (override.overrideType === 'unlimited' ? 'unlimited' : 'block');
      const variant = variantsFor(override).find(v => v.id === draft.variantId);
      const response = await api.put(path, {
        productId: draft.productId,
        productTitle: draft.productTitle,
        overrideType,
        newLimit: draft.newLimit,
        reason: draft.reason,
        expiresAt: draft.expiresAt || null,
        variantId: draft.variantId,
        variantTitle: draft.variantId ? (variant ? variant.title : override.variantTitle || '') : ''
      });
      setDrafts(prev => {
        const next = { ...prev };
        delete next[override._id];
        return next;
      });
      showToast(savedMessage(response, 'Customer override updated successfully'));
      loadOverrides();
    } catch (err) {
      console.error('Update override error:', err);
      setError(err.status === 409 ? err.message : 'Failed to update customer override');
    } finally {
      setSavingId(null);
    }
  };

  // Opening the confirmation modal replaces the native window.confirm, whose heading
  // ("An embedded page at … says") is fixed by the browser and can't be branded.
  const handleDelete = (override) => {
    setOverrideToDelete(override);
    setDeleteModalActive(true);
  };

  const handleDeleteCancel = () => {
    setDeleteModalActive(false);
    setOverrideToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    const override = overrideToDelete;
    if (!override) return;

    // The dialog stays open, its Remove button spinning, until the removal is done.
    try {
      const path = override.targetType === 'tag'
        ? `/customers/tag-override/${override._id}`
        : `/customers/${override.customerId}/override/${override._id}`;
      const response = await api.delete(path);
      setOverrides(prev => prev.filter(o => o._id !== override._id));
      showToast(savedMessage(response, 'Customer override removed successfully'));
    } catch (err) {
      console.error('Delete override error:', err);
      setError('Failed to remove customer override');
    } finally {
      setDeleteModalActive(false);
      setOverrideToDelete(null);
    }
  };

  const showToast = (message) => {
    setToastMessage(message);
    setToastActive(true);
  };

  if (loading && overrides.length === 0) {
    return (
      <Page fullWidth title="Customer Overrides">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // The variants the row can choose from: those of the product it's being moved
  // to, if a different one has been picked, otherwise its own product's.
  const variantsFor = (override) => {
    const draft = draftFor(override);
    if (draft.productId === override.productId) return override.productVariants || [];
    const variants = draft.productVariants || [];
    return variantOptionsFor(variants).length > 0 ? variants : [];
  };

  // Moving an override to another product starts it on all of that product's
  // variants; the full variant list replaces the few the search returned once it
  // loads (unless another product has been picked in the meantime).
  const handleProductPick = (override, product) => {
    updateDraft(override, {
      productId: product.id,
      productTitle: product.title,
      variantId: '',
      productVariants: product.variants || []
    });
    loadAllVariants(product)
      .then((variants) => {
        setDrafts(prev => (prev[override._id]?.productId === product.id
          ? { ...prev, [override._id]: { ...prev[override._id], productVariants: variants } }
          : prev));
      })
      .catch((err) => {
        console.error('Load variants error:', err);
        setError('Failed to load all of this product\'s variants. Please pick the product again.');
      });
  };

  const variantCell = (override) => {
    const draft = draftFor(override);
    const variants = variantsFor(override);
    if (variants.length === 0) {
      if (draft.productId !== override.productId) return '-';
      return override.variantTitle || (override.productHasVariants ? 'All variants' : '-');
    }
    const options = [
      { label: 'All variants', value: '' },
      ...variants.map(v => ({ label: v.title, value: v.id }))
    ];
    // A variant since removed from the product still shows as the current choice.
    if (draft.variantId && !variants.some(v => v.id === draft.variantId)) {
      options.push({ label: override.variantTitle || 'Removed variant', value: draft.variantId });
    }
    return (
      <div style={{ minWidth: '160px' }}>
        <VariantSelect
          options={options}
          value={draft.variantId}
          onChange={(value) => updateDraft(override, { variantId: value })}
        />
      </div>
    );
  };

  // The reason reads as plain text; clicking it turns it into a text box, which
  // goes back to text when it loses focus.
  const reasonCell = (override) => {
    const draft = draftFor(override);
    if (editingReasonId === override._id) {
      return (
        <div style={{ minWidth: '200px' }}>
          <TextField
            label="Reason"
            labelHidden
            value={draft.reason}
            onChange={(value) => updateDraft(override, { reason: value })}
            onBlur={() => setEditingReasonId(null)}
            placeholder="Why is this override needed?"
            autoComplete="off"
            autoFocus
          />
        </div>
      );
    }
    return (
      <button
        type="button"
        className="og-inline-edit"
        title={draft.reason || undefined}
        onClick={() => setEditingReasonId(override._id)}
      >
        {draft.reason
          ? <Text as="span">{draft.reason}</Text>
          : <Text as="span" color="subdued">Add reason</Text>}
      </button>
    );
  };

  const tableRows = overrides.map((override) => {
    const draft = draftFor(override);
    const dirty = isDirty(override);
    return [
      // A tag override shows its tag. A customer override shows the customer's name;
      // hover reveals the email. Falls back to email for overrides saved before names
      // were captured.
      override.targetType === 'tag' ? (
        <Text as="span">Tag: <Text as="span" fontWeight="semibold">{displayTag(override.tag, customerTags)}</Text></Text>
      ) : (
        <Tooltip content={override.customerEmail || 'No email on file'}>
          <Text as="span">{override.customerName || override.customerEmail || '(unknown customer)'}</Text>
        </Tooltip>
      ),
      (
        <div>
          {override.productTag ? (
            <Text as="span">Tag: <Text as="span" fontWeight="semibold">{displayTag(override.productTag, productTags)}</Text></Text>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Text as="span">{draft.productTitle || '(unknown product)'}</Text>
              <ProductPicker onChange={(product) => handleProductPick(override, product)} />
            </div>
          )}
          {/* The base limit belongs to the saved product; it's shown again after saving. */}
          {draft.productId === override.productId && typeof override.baseLimit === 'number' && (
            <div>
              <Text as="span" color="subdued">Base limit: {override.baseLimit}</Text>
            </div>
          )}
        </div>
      ),
      center(override.productTag ? '-' : variantCell(override)),
      center(
        <div style={{ width: '90px' }}>
          <TextField
            label="New limit"
            labelHidden
            type="number"
            value={draft.newLimit}
            onChange={(value) => updateDraft(override, { newLimit: value })}
            placeholder={override.overrideType === 'unlimited' ? 'Unlimited' : 'Blocked'}
            min="0"
            autoComplete="off"
          />
        </div>
      ),
      center(
        <ExpiryPicker
          value={draft.expiresAt}
          onChange={(value) => updateDraft(override, { expiresAt: value })}
        />
      ),
      reasonCell(override),
      // Remove, then Save at the far right. Save's space is always kept (it's only
      // hidden until something changes), so nothing in the row moves when it appears.
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px' }}>
        <Button
          plain
          icon={DeleteMajor}
          onClick={() => handleDelete(override)}
          accessibilityLabel="Remove override"
        />
        <div style={{ visibility: dirty ? 'visible' : 'hidden' }} aria-hidden={!dirty}>
          <Button
            size="slim"
            primary
            loading={savingId === override._id}
            disabled={!dirty}
            onClick={() => handleRowSave(override)}
          >
            Save
          </Button>
        </div>
      </div>
    ];
  });

  return (
    <Page
      fullWidth
      title="Customer Overrides"
      subtitle="Manage special limits and exceptions for specific customers"
      primaryAction={{
        content: 'Add Override',
        onAction: handleModalOpen
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner status="critical" title="Error">
              <p>{error}</p>
              <Button onClick={loadOverrides}>Retry</Button>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            {overrides.length > 0 ? (
              <DataTable
                columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                headings={['Customer/Tag', 'Product/Tag', center('Variant'), center('New Limit'), center('Expiry'), 'Reason', '']}
                rows={tableRows}
              />
            ) : (
              <EmptyState
                heading="No customer overrides configured"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>
                  Create special exceptions for specific customers who need different purchase limits.
                </p>
              </EmptyState>
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add Override Modal */}
      <Modal
        open={modalActive}
        onClose={handleModalClose}
        title="Add Override"
        primaryAction={{
          content: 'Save Override',
          onAction: whileBusy(handleSave),
          loading: dialogBusy,
          disabled: (targetType === 'tag' ? !tag : !formData.customerEmail)
            || (coverType === 'tag' ? !productTag : !formData.productId || !formData.productTitle)
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleModalClose
          }
        ]}
      >
        <Modal.Section>
          {/* Every field is a direct child of FormLayout so each gets the same
              spacing: FormLayout spaces its children, and anything grouped inside
              a fragment is treated as one row with no gaps between its parts. */}
          <FormLayout>
            <ButtonGroup segmented>
              <Button primary={targetType === 'customer'} onClick={() => setTargetType('customer')}>
                Customer
              </Button>
              <Button primary={targetType === 'tag'} onClick={() => setTargetType('tag')}>
                Customer tag
              </Button>
            </ButtonGroup>

            {targetType === 'tag' && (
              <TagPicker
                kind="customer"
                label="Customer tag"
                value={tag}
                onChange={setTag}
              />
            )}

            {targetType !== 'tag' && (
              <div>
                <Autocomplete
                  options={customerOptions}
                  selected={selectedCustomer ? [selectedCustomer.value] : []}
                  onSelect={handleCustomerSelect}
                  loading={customerSearchLoading}
                  emptyState={customerSearchEmpty ? (
                    <div style={{ padding: '12px 16px', color: '#6b7280' }}>
                      No customers found
                    </div>
                  ) : null}
                  textField={
                    <Autocomplete.TextField
                      label="Customer"
                      value={customerSearchQuery}
                      onChange={(value) => {
                        setCustomerSearchQuery(value);
                        // Flip on the loading state synchronously with the keystroke.
                        // Polaris only opens the options popover on a keystroke while
                        // the ComboBox already has a child (a loading row counts), so
                        // without this the async results arrive with the popover shut
                        // and don't appear until the next keystroke.
                        setCustomerSearchLoading(value.trim().length >= 2);
                      }}
                      placeholder="Start typing customer name or email..."
                      autoComplete="off"
                    />
                  }
                />

                {selectedCustomer && (
                  <Text as="p" color="subdued">
                    Selected: {selectedCustomer.label}
                  </Text>
                )}
              </div>
            )}

            <ButtonGroup segmented>
              <Button primary={coverType === 'product'} onClick={() => setCoverType('product')}>
                Product
              </Button>
              <Button primary={coverType === 'tag'} onClick={() => setCoverType('tag')}>
                Product tag
              </Button>
            </ButtonGroup>

            {coverType === 'tag' && (
              <TagPicker
                kind="product"
                label="Product tag"
                value={productTag}
                onChange={setProductTag}
              />
            )}

            {coverType === 'product' && (
            <div>
                <Autocomplete
                  options={productOptions}
                  selected={selectedProduct ? [selectedProduct.value] : []}
                  onSelect={handleProductSelect}
                  loading={productSearchLoading}
                  emptyState={productSearchEmpty ? (
                    <div style={{ padding: '12px 16px', color: '#6b7280' }}>
                      No products found
                    </div>
                  ) : null}
                  textField={
                    <Autocomplete.TextField
                      label="Product"
                      value={productSearchQuery}
                      onChange={(value) => {
                        setProductSearchQuery(value);
                        // Same as the customer field: open the popover on the keystroke
                        // so async results aren't hidden until the next keypress.
                        setProductSearchLoading(value.trim().length >= 2);
                      }}
                      placeholder="Start typing to search products..."
                      autoComplete="off"
                    />
                  }
                />

                {selectedProduct && (
                  <Text as="p" color="subdued">
                    Selected: {selectedProduct.label}
                  </Text>
                )}
              </div>
            )}

            {coverType === 'product' && selectedProduct && variantOptions.length > 0 && (
              <Autocomplete
                options={variantOptions}
                selected={selectedVariant ? [selectedVariant.value] : []}
                onSelect={handleVariantSelect}
                textField={
                  <Autocomplete.TextField
                    label="Variant"
                    value={selectedVariant ? selectedVariant.label : ''}
                    onChange={() => {}}
                    placeholder="Select a variant or choose 'All Variants'"
                    autoComplete="off"
                  />
                }
              />
            )}

            <TextField
              label="New Limit (leave empty to block)"
              type="number"
              value={formData.newLimit}
              onChange={(value) => setFormData({ ...formData, newLimit: value })}
              placeholder="Enter new limit quantity or leave empty to block"
              min="0"
            />

            <TextField
              label="Reason (optional)"
              value={formData.reason}
              onChange={(value) => setFormData({ ...formData, reason: value })}
              placeholder="Why is this override needed?"
              multiline={2}
            />

            <TextField
              label="Expiry date (optional)"
              type="date"
              value={formData.expiresAt}
              onChange={(value) => setFormData({ ...formData, expiresAt: value })}
              helpText="Leave empty for an override that never expires."
              clearButton
              onClearButtonClick={() => setFormData({ ...formData, expiresAt: '' })}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Remove Override confirmation */}
      <Modal
        open={deleteModalActive}
        onClose={handleDeleteCancel}
        title="Remove customer override"
        primaryAction={{
          content: 'Remove',
          destructive: true,
          onAction: whileBusy(handleDeleteConfirm),
          loading: dialogBusy
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleDeleteCancel
          }
        ]}
      >
        <Modal.Section>
          <div className="og-confirm-modal">
            <Text as="p">
              Are you sure?
            </Text>
          </div>
        </Modal.Section>
      </Modal>

      {toastActive && (
        <Toast
          content={toastMessage}
          onDismiss={() => setToastActive(false)}
        />
      )}
    </Page>
  );
}

export default Customers;
